import React, { FC, memo, useMemo, useState, useEffect } from 'react'
import { AnyStyledComponent } from 'styled-components'

import { FilterIconImage } from './filter-icon-styles'

// Lazy load icons only when needed
const iconMap: Record<string, () => Promise<{ default: React.ComponentType<any> }>> = {
    Waschmaschine: () => import('../../assets/icons/filter-icons/washing-machine.svg'),
    Geschirrspüler: () => import('../../assets/icons/filter-icons/dishwasher.svg'),
    Fernseher: () => import('../../assets/icons/filter-icons/television.svg'),
    WLAN: () => import('../../assets/icons/filter-icons/wifi.svg'),
    Sauna: () => import('../../assets/icons/filter-icons/sauna.svg'),
    Swimmingpool: () => import('../../assets/icons/filter-icons/pool.svg'),
    Garten: () => import('../../assets/icons/filter-icons/garden.svg'),
    Terrasse: () => import('../../assets/icons/filter-icons/terrace.svg'),
    Balkon: () => import('../../assets/icons/filter-icons/balcony.svg'),
    Grill: () => import('../../assets/icons/filter-icons/grill.svg'),
    Babybett: () => import('../../assets/icons/filter-icons/babybett.svg'),
    Klimaanlage: () => import('../../assets/icons/filter-icons/ac.svg'),
}

// Default fallback
const getDefaultIcon = () => import('../../assets/icons/filter-icons/ac.svg')

interface Props {
    filterKey: string
    alt?: string
}

export const getFilterIconComponent = (filterKey: string) => {
    return iconMap[filterKey] || getDefaultIcon
}

const FilterIcon: FC<Props> = ({ filterKey, ...restProps }) => {
    const [IconComponent, setIconComponent] = useState<React.ComponentType<any> | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const iconLoader = useMemo(() => getFilterIconComponent(filterKey), [filterKey])

    useEffect(() => {
        let mounted = true

        iconLoader()
            .then(module => {
                if (mounted) {
                    setIconComponent(() => module.default)
                    setIsLoading(false)
                }
            })
            .catch(() => {
                if (mounted) {
                    // Fallback to default icon on error
                    getDefaultIcon().then(module => {
                        if (mounted) {
                            setIconComponent(() => module.default)
                            setIsLoading(false)
                        }
                    })
                }
            })

        return () => {
            mounted = false
        }
    }, [iconLoader])

    if (isLoading) {
        // Return a placeholder or loading state
        return <FilterIconImage {...restProps} style={{ opacity: 0.5 }} />
    }

    return IconComponent ? <FilterIconImage {...restProps} as={IconComponent as AnyStyledComponent} /> : null
}

export default memo(FilterIcon)
